/**
 * Admin selectors (round-22 Phase D — Cognito-group-gated).
 *
 * Thin reads over `state.admin` for the per-record edit
 * overrides, plus a JWT-derived `selectIsAdmin` selector that
 * inspects the auth slice's ID token's `cognito:groups` claim
 * per round-22 Phase D. The pre-round-22 client-toggleable
 * admin flag has been removed — the admin capability is now
 * granted OUT-OF-BAND by the operator via `grant-admin.sh` and
 * enforced at the Cognito authorizer on the admin API gateway.
 *
 * See `src/lib/auth/useIsAdmin.ts` for the React-hook variant
 * used by function components; this selector is for cases where
 * a plain state read is more ergonomic (component tests,
 * connect-style consumers).
 */

import type { RootState } from '../index';
import { decodeJwt } from '../../lib/auth/jwt-decoder';
import type {
  AdminEdit,
  AdminSaveState,
  FactEdit,
  NewsEdit,
  ReleaseEdit,
} from '../slices/admin-slice';

/**
 * True iff the current session's ID token has `admin` in
 * `cognito:groups`. False for unauthenticated visitors and
 * authenticated non-admins alike.
 */
export function selectIsAdmin(state: RootState): boolean {
  const idToken = state.auth.idToken;
  if (idToken === undefined || idToken.length === 0) return false;
  const payload = decodeJwt(idToken);
  if (payload === null) return false;
  const groups = payload['cognito:groups'];
  if (!Array.isArray(groups)) return false;
  return groups.includes('admin');
}

/** Get the staged admin edit (any kind) for a content id, if any. */
export function selectAdminEditForId(
  state: RootState,
  id: string | undefined,
): AdminEdit | undefined {
  if (!id) return undefined;
  return state.admin.editsById[id];
}

/** Get the staged news edit for a content id, narrowed by `kind`. */
export function selectNewsEditForId(
  state: RootState,
  id: string | undefined,
): NewsEdit | undefined {
  const edit = selectAdminEditForId(state, id);
  return edit?.kind === 'news' ? edit : undefined;
}

/** Get the staged fact edit for a content id, narrowed by `kind`. */
export function selectFactEditForId(
  state: RootState,
  id: string | undefined,
): FactEdit | undefined {
  const edit = selectAdminEditForId(state, id);
  return edit?.kind === 'fact' ? edit : undefined;
}

/** Get the staged release edit for a content id, narrowed by `kind`. */
export function selectReleaseEditForId(
  state: RootState,
  id: string | undefined,
): ReleaseEdit | undefined {
  const edit = selectAdminEditForId(state, id);
  return edit?.kind === 'release' ? edit : undefined;
}

/**
 * Get the save-to-server state for a content id, or the idle
 * default when no save has been attempted. Consumed by the
 * DailyPage edit drawers to render a spinner / error banner while
 * a `PostContentV1` round-trip is in flight
 * (`content-ingestion-pipeline` Sub-R5.1).
 */
export function selectAdminSaveState(
  state: RootState,
  id: string | undefined,
): AdminSaveState {
  if (!id) return { status: 'idle' };
  return state.admin.saveById[id] ?? { status: 'idle' };
}
